<!--
  A compare's header (views/CompareView.svelte): the base it is against
  and the head (with its fork, when it is one), each a link to its history with a caret changing it
  (components/RefPicker.svelte, `, b` and `, h`), and the compare reversed; how far the head is ahead
  and behind, the head's checks, the open pull request from the head when
  there is one (else the way to open one on GitHub), the compare's two views,
  and its local agents. Everything it shows is the store's.
-->
<script lang="ts">
  import type { CompareStore } from '../state/compare.svelte';
  import { checksState, failingCount } from '../logic/checks';
  import { subjectKey, type SubjectKey } from '../api/relaySubject';
  import { compareHead } from '../subject';
  import { compareSpec } from '../state/compare/fetch';
  import { prPath, subjectPath } from '../router';
  import { carryFilter } from '../logic/filefilter';
  import { nav } from '../nav.svelte';
  import { session } from '../state/session.svelte';
  import { swapPath, type PickAt } from '../jump/pick';
  import RelayPrAgents from './RelayPrAgents.svelte';
  import RefPicker from './RefPicker.svelte';

  interface Props {
    store: CompareStore;
    view: 'files' | 'commits';
    /** The picker open (the page opens one by key), or null. */
    picking?: 'base' | 'head' | null;
  }
  let { store, view, picking = $bindable(null) }: Props = $props();

  const s = $derived(store.subject);
  const c = $derived(store.compare);
  const base = $derived(c?.baseRef ?? s.base ?? 'default branch');
  /** The head's fork, as GitHub names it (`ana`, `ana/fork`); null for a head in this repository. */
  const fork = $derived(s.headRepo ? `${s.headRepo.owner}${s.headRepo.repo === null ? '' : '/' + s.headRepo.repo}` : null);
  const webUrl = $derived(session.config?.github_web_url ?? 'https://github.com');
  /** GitHub's page to open a pull request from the head into the base. */
  const openPull = $derived(c ? `${webUrl}/${s.owner}/${s.repo}/compare/${compareSpec(s, c.baseRef)}?expand=1` : null);
  /** The open pull request from the head into the base, else any open one from the head. */
  const pull = $derived(store.pulls?.[0] ?? null);
  // Check runs and commit statuses together, by the one rule (logic/checks.ts).
  const checks = $derived(store.checks ? checksState(store.checks.contexts) : null);
  const failing = $derived(store.checks ? failingCount(store.checks.contexts) : 0);
  const key = $derived(subjectKey(s) as SubjectKey);
  /** A branch's history page in `owner/repo`. */
  const history = (owner: string, repo: string, ref: string) => subjectPath({ kind: 'ref', owner, repo, ref, path: null });
  /** The head's history, in the repository it lives in; null for a fork named by its owner alone (GitHub finds it, Changeling cannot name it). */
  const headHistory = $derived(!s.headRepo ? history(s.owner, s.repo, s.head) : s.headRepo.repo === null ? null : history(s.headRepo.owner, s.headRepo.repo, s.head));
  let agentsOpen = $state(false);
  /** Where each picker is: this compare, on this view, with the page's Files filter. */
  const pickAt = (end: 'base' | 'head'): PickAt => ({ subject: s, end, view, query: nav.query, defaultBranch: c?.baseRef ?? null });
  /** The compare the other way round; none for a fork's head, which cannot be a base. */
  const swap = $derived(swapPath(s, c?.baseRef ?? null, view, nav.query));
  const baseName = $derived(c?.baseRef ?? s.base);

  function standing(): string {
    if (!c) return '';
    if (c.status === 'identical') return `${base} and the head are the same`;
    const ahead = `${c.aheadBy} commit${c.aheadBy === 1 ? '' : 's'} ahead`;
    if (!c.behindBy) return ahead;
    return `${c.aheadBy ? ahead + ', ' : ''}${c.behindBy} behind`;
  }
  const tabs = $derived([
    { view: 'files' as const, label: 'Files', count: c ? c.files.length : null },
    { view: 'commits' as const, label: 'Commits', count: c ? c.totalCommits : null },
  ]);
</script>

<header class="pr-head compare-head">
  <div class="pr-head-row">
    <div style="min-width:0;display:grid;gap:8px">
      <!-- Named apart from its controls: the same name whether a picker is open or not. -->
      <h1 class="pr-title" aria-label="Compare: base {base}, from head {compareHead(s)}">
        <RefPicker
          label={base}
          pick={pickAt('base')}
          href={baseName ? history(s.owner, s.repo, baseName) : null}
          bind:open={() => picking === 'base', (v) => (picking = v ? 'base' : picking === 'base' ? null : picking)}
        />
        <span class="muted" aria-label="from">←</span>
        <RefPicker
          label={s.head}
          pick={pickAt('head')}
          href={headHistory}
          bind:open={() => picking === 'head', (v) => (picking = v ? 'head' : picking === 'head' ? null : picking)}
        />
      </h1>
      <div class="pr-sub">
        <span>Compare</span>
        {#if fork}<span class="pill">From fork <b>{fork}</b></span>{/if}
        {#if c}<span class="standing">{standing()}</span>{/if}
        {#if checks && checks !== 'none'}
          <span class="pill checks-{checks}">{checks === 'success' ? 'Checks passed' : checks === 'failure' ? `${failing} check${failing === 1 ? '' : 's'} failing` : 'Checks running'}</span>
        {/if}
        {#if c?.truncated}<span class="muted">The newest {c.commits.length} of {c.totalCommits} commits are shown</span>{/if}
      </div>
    </div>
    <div class="pr-actions">
      {#if swap}<a class="btn small" href={swap} title="Reverse the compare: {compareHead(s)} as the base, {base} as the head">Reverse compare</a>{/if}
      {#if session.agents}
        <button class="btn small" aria-expanded={agentsOpen} aria-controls="compare-agents" onclick={() => (agentsOpen = !agentsOpen)}>Local agents</button>
      {/if}
      {#if pull}
        <a class="btn small" href={prPath(s.owner, s.repo, pull.number)} title={pull.title}>Pull request #{pull.number}{pull.base === base ? '' : ` (into ${pull.base})`}</a>
      {:else if openPull && c && c.aheadBy > 0}
        <a class="btn small primary" href={openPull} target="_blank" rel="noreferrer" data-external>Open a pull request ↗</a>
      {/if}
    </div>
  </div>
  {#if agentsOpen && session.agents}<div id="compare-agents"><RelayPrAgents subject={key} embedded /></div>{/if}
  <nav class="compare-tabs" aria-label="Compare views">
    {#each tabs as t (t.view)}
      <a href={carryFilter(subjectPath(s, t.view), nav.query)} aria-current={t.view === view ? 'page' : undefined}>{t.label}{#if t.count !== null}{' '}<span class="count">{t.count}</span>{/if}</a>
    {/each}
  </nav>
</header>

<style>
  .compare-tabs {
    display: flex;
    gap: 4px;
    border-top: 1px solid var(--line);
    padding-top: 10px;
  }
  .compare-tabs a {
    padding: 4px 10px;
    border-radius: 6px;
    color: var(--fg);
    text-decoration: none;
    font-size: 13px;
  }
  .compare-tabs a[aria-current='page'] {
    background: var(--code);
    font-weight: 650;
  }
  .count {
    color: var(--muted);
    font-size: 12px;
  }
  .checks-success {
    color: var(--good);
  }
  .checks-failure {
    color: var(--bad);
  }
</style>
